import {
  Button,
  ButtonVariants,
  FormControlSizes,
  isTerminalQuoteStatus,
  Tooltip,
  useCallbackRef,
  useGlobalToasts,
  useSocketClient,
} from '@talos/kyoko';
import { useMemo, useState } from 'react';

import { cancelMultipleQuotes } from '../cancelMultipleQuotes';
import { useRFQCancellationConfirmation } from '../otherUserNegotiatedRFQTradeConfirmation';
import { useQuoteSweepingQuoteObservable, type QuoteSweepingQuoteMarkets } from './useQuoteSweepingQuoteMarkets';

interface RFQSweepingCloseRemainderButtonProps {
  correlationID: string | undefined;
  quoteSweepingData: QuoteSweepingQuoteMarkets;
  disabled?: boolean;
  disabledReason?: string;
}

/**
 * Two-step destructive action for cancelling every remaining live quote in the sweep group.
 *
 * This is separate from the regular panel close button: it does not dismiss the sweeper,
 * it cancels the still-cancellable quote rows that would otherwise remain open after a
 * partial or abandoned sweep.
 */
export function RFQSweepingCloseRemainderButton({
  correlationID,
  quoteSweepingData,
  disabled = false,
  disabledReason,
}: RFQSweepingCloseRemainderButtonProps) {
  const webSocketClient = useSocketClient();
  const { add: addToast } = useGlobalToasts();
  const quoteDataForSweepGroupObservable = useQuoteSweepingQuoteObservable(
    correlationID,
    'RFQSweepingCloseRemainderButton'
  );
  const [staged, setStaged] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  const closeRemainderQuotes = useMemo(
    () =>
      quoteSweepingData.sweeperQuotes
        .map(({ quote }) => quote)
        // Terminal quotes have either completed or can no longer be acted on by cancelQuote.
        .filter(quote => !isTerminalQuoteStatus(quote.QuoteStatus)),
    [quoteSweepingData.sweeperQuotes]
  );

  const closeRemainderDisabled = closeRemainderQuotes.length === 0 || disabled || isClosing;
  const tooltip =
    disabledReason ??
    (closeRemainderQuotes.length === 0 ? 'There are no cancellable quotes to close for this sweep group.' : undefined);

  const closeRemainder = useCallbackRef(async () => {
    setIsClosing(true);
    try {
      await cancelMultipleQuotes(closeRemainderQuotes, {
        webSocketClient,
        quoteSub: quoteDataForSweepGroupObservable,
        addToast,
      });
    } finally {
      setIsClosing(false);
      setStaged(false);
    }
  });

  const { execute: closeRemainderWithConfirmation, dialog: closeRemainderConfirmationDialog } =
    useRFQCancellationConfirmation({
      quote: closeRemainderQuotes.at(0),
      execute: closeRemainder,
      usePortal: false,
    });

  const handleClick = useCallbackRef(() => {
    if (closeRemainderDisabled) {
      return;
    }

    if (!staged) {
      setStaged(true);
      return;
    }

    closeRemainderWithConfirmation();
  });

  return (
    <>
      <Tooltip tooltip={tooltip} disabled={!tooltip}>
        <Button
          size={FormControlSizes.Small}
          onClick={handleClick}
          disabled={closeRemainderDisabled}
          loading={isClosing}
          variant={ButtonVariants.Negative}
          onMouseLeave={() => setStaged(false)}
          // keep width static so it stays the same width when staged, 112 is enough to render the Cancel remaining label
          width="112px"
          data-testid="rfq-sweeping-close-remainder-button"
        >
          {staged ? 'Confirm' : 'Cancel remaining'}
        </Button>
      </Tooltip>
      {closeRemainderConfirmationDialog}
    </>
  );
}
